/**
 * `/stories/$uid?step=generate|edit|play` (§3.2).
 *
 * `step=play` is the kid player and nothing else: `StoryPlayer` in `kid` mode covers the viewport,
 * the grown-up panel's **Exit** drops the `step` search param, and the studio chrome comes back.
 *
 * `step=generate` is where the wizard lands: the real progress of the story (its job's phases,
 * then the narration), and the complete story opens automatically when ready. A longer story
 * can optionally be previewed before it is finished (docs/STORY-STREAMING.md): the server
 * publishes it part by part and the preview picks up each new part as it lands. Automatic
 * playback waits for all scenes to avoid generation pauses. Once up, the player stays until
 * **Exit**, whatever the row does meanwhile
 * (`generating → ready`, or `→ failed`: it plays the parts that were made, then says so). A story
 * that failed says so in plain words and offers **Try again** (the same request) and **Change
 * something** (the wizard, pre-filled). A ready story's page makes **Play** the one big action.
 */
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Download, EllipsisVertical, Pencil, Play, RotateCcw, RotateCw, Sparkles, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { keys } from '@/api/keys'
import { puppetsQuery } from '@/api/puppets'
import { createStory, exportUrl, getStory, refreshStoryAssets, requestOf, storyError, streamReady, type StoryRow } from '@/api/stories'
import { StoryPlayer, evictStoryPrefetch, prefetchStory } from '@/components/StoryPlayer'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { toast } from '@/components/ui/toast'
import { BOOKMARK_STORY_MUTATION, BookmarkButton } from '@/features/story/BookmarkButton'
import { DELETE_STORIES_MUTATION, DeleteStoryDialog } from '@/features/story/DeleteStoryDialog'
import { StoryPhases, useStoryProgress } from '@/features/story/StoryProgress'
import { storyLengthLine } from '@/features/story/StoryList'
import { StoryThumb } from '@/features/story/StoryThumb'
import { StoryMetrics } from '@/features/story/StoryMetrics'
import { GenerationModeBadge } from '@/features/story/GenerationModeBadge'
import { LessonDetails } from '@/features/story/LessonPicker'
import { TopicBadge } from '@/features/story/TopicBadge'
import { VideoExport } from '@/features/story/VideoExport'

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)
export const STORY_STEPS = ['generate', 'edit', 'play'] as const
export type StoryStep = (typeof STORY_STEPS)[number]
export const storySearchSchema = z.object({ step: lenient(z.enum(STORY_STEPS)), scene: lenient(z.string()) })
export type StorySearch = z.infer<typeof storySearchSchema>
export function validateStorySearch(input: Record<string, unknown>): StorySearch {
  return storySearchSchema.parse(input)
}
const route = getRouteApi('/stories/$uid')

/** The wizard link that makes another story like this one ("Change something", "Same friends"). */
export function wizardSearchFor(row: StoryRow, opts: { idea?: boolean } = {}) {
  const req = requestOf(row)
  return {
    cast: req.cast.map((c) => c.uid).join(',') || undefined,
    world: req.world,
    band: req.age_band,
    generation_mode: req.generation_mode,
    authoring_version: req.authoring_version,
    idea: opts.idea ? req.prompt : undefined,
  }
}

export function StoryPage() {
  const { uid } = route.useParams()
  // One page per story: "Try again" lands on a new uid, and nothing of the old story's page (its
  // auto-open, its player kept up, its furthest step) may carry over to the new one.
  return <StoryPageFor key={uid} uid={uid} />
}

function StoryPageFor({ uid }: { uid: string }) {
  const search = route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  // While a delete is in flight, stop polling: the next poll would 404 and flash an error.
  const deleting = useIsMutating({ mutationKey: DELETE_STORIES_MUTATION }) > 0
  // Nor while a bookmark is being saved: a poll landing first would flash the old state.
  const bookmarking = useIsMutating({ mutationKey: BOOKMARK_STORY_MUTATION }) > 0
  const onPlay = search.step === 'play'
  // The player, once up, stays up until Exit — through `generating → ready` and `generating →
  // failed` alike (the player then plays what was made and ends it). Leaving `step=play` lets go.
  const playerUp = useRef(false)
  if (!onPlay) playerUp.current = false
  // The player covers the page for this row: the row is not polled under it (the player watches
  // its own bundle for new parts) and the job is not followed live, only asked for now and then
  // (nothing of it is on screen, but a job that dies there must still end the player).
  const covered = (row: StoryRow | undefined): boolean =>
    onPlay && (playerUp.current || row?.status === 'ready' || (row?.status === 'generating' && streamReady(row) && row.stream?.state !== 'failed'))
  const story = useQuery({
    queryKey: keys.stories.detail(uid),
    queryFn: ({ signal }) => getStory(uid, signal),
    refetchInterval: (q) => {
      const row = q.state.data
      const generating = row?.status === 'generating' || row?.generation_state === 'running' || row?.generation_state === 'queued'
      return !deleting && !bookmarking && !covered(row) && generating ? 1500 : false
    },
  })
  const progress = useStoryProgress(story.data, { follow: !covered(story.data) })

  const leavePlayer = () => {
    void navigate({ to: '/stories/$uid', params: { uid }, search: {} })
    // The row was not polled while the player was up: fetch it now, not at the next poll.
    void qc.invalidateQueries({ queryKey: keys.stories.detail(uid) })
  }
  // Leave first, then drop the cached story, so this page never re-renders without it.
  const afterDelete = () => void navigate({ to: '/stories' }).then(() => qc.removeQueries({ queryKey: keys.stories.detail(uid) }))

  const retry = useMutation({
    // A bookmarked story's retry starts bookmarked too (the failed one keeps its own bookmark).
    mutationFn: (row: StoryRow) => createStory({ ...requestOf(row), ...(row.bookmarked ? { bookmarked: true } : {}) }),
    onSuccess: ({ story: next }) => {
      void qc.invalidateQueries({ queryKey: keys.stories.all() })
      void navigate({ to: '/stories/$uid', params: { uid: next.uid }, search: { step: 'generate' } })
    },
  })

  const refresh = useMutation({
    mutationFn: () => refreshStoryAssets(uid),
    onSuccess: (result) => {
      evictStoryPrefetch(uid)
      void qc.invalidateQueries({ queryKey: keys.stories.detail(uid) })
      void qc.invalidateQueries({ queryKey: keys.stories.all() })
      toast.success(result.preserved_verified_pins ? 'Verified story restored' : 'Story assets refreshed', { description: result.notice ?? 'Updated animations, puppets, and scenery are ready.' })
      prefetchStory(uid, { fresh: true })
    },
    onError: (err) => {
      toast.error('Could not refresh assets', { description: err instanceof Error ? err.message : String(err) })
    },
  })

  // Part 1 of a story still being made is out: have its bundle, first scene and the player's code
  // on the way before anyone opens its optional preview. Not under a
  // player already up: it has its own copy, and a second one would wait in the prefetch cache for
  // the next open — the finished story's "Play" among them — with a revision already old.
  const firstPartOut = !onPlay && story.data?.status === 'generating' && progress.playable
  useEffect(() => {
    if (firstPartOut) prefetchStory(uid)
  }, [firstPartOut, uid])

  // Normal playback is uninterrupted: wait for every scene and its voices. Early
  // previews remain opt-in, and their partial prefetch must not open the final story.
  const started = useRef(false)
  useEffect(() => {
    if (search.step !== 'generate' || story.data?.status !== 'ready' || !progress.playable || started.current) return
    started.current = true
    evictStoryPrefetch(uid)
    void navigate({ to: '/stories/$uid', params: { uid }, search: { step: 'play' }, replace: true })
  }, [search.step, story.data?.status, progress.playable, navigate, uid])

  // The kid player: full-screen, no studio chrome, one way out. It opens from the bundle alone, so
  // it does not wait for the story row first (one more round trip before the bundle was even
  // asked for), nor for the job of a story still being made; a row that turns out not to be
  // playable yet, or whose job died, takes the page back. Only once it is known to be playable is
  // the player pinned (`playerUp`), and from then on it stays until Exit. Under it, a job that
  // ends without the bundle saying so (cancelled, or lost with its worker) tells it to stop
  // waiting for more parts.
  const status = story.data?.status
  const canPlay = status === 'ready' || (status === 'generating' && progress.playable)
  if (onPlay && (story.isLoading || progress.jobPending || canPlay || playerUp.current)) {
    if (canPlay) playerUp.current = true
    return <StoryPlayer uid={uid} mode="kid" onExit={leavePlayer} streamEnded={progress.jobFailed !== null} />
  }
  if (story.isLoading) return <div className="grid min-h-[60vh] place-items-center text-ink-2">Opening the story…</div>
  if (story.error || !story.data)
    return (
      <div className="mx-auto flex max-w-xl flex-col items-start gap-4 p-8" role="alert">
        <p className="text-lg text-ink">This story could not be opened.</p>
        <Button asChild variant="secondary">
          <Link to="/stories">All stories</Link>
        </Button>
      </div>
    )

  const row = story.data
  const ready = row.status === 'ready'
  const failed = row.status === 'failed' || progress.jobFailed !== null

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="More">
          <EllipsisVertical aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {ready ? (
          <>
            <DropdownMenuItem onSelect={() => refresh.mutate()} disabled={refresh.isPending}>
              <RotateCw className="size-4" aria-hidden />
              Refresh assets
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={exportUrl(uid)} download>
                <Download className="size-4" aria-hidden />
                Download to keep
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem destructive onSelect={() => setConfirmingDelete(true)}>
          <Trash2 className="size-4" aria-hidden />
          Delete story…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-8">
      <div className="flex items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/stories">
            <ArrowLeft aria-hidden />
            All stories
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          {ready ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => refresh.mutate()}
              loading={refresh.isPending}
              disabled={refresh.isPending}
              title="Refresh story assets (puppets, animations, stages)"
            >
              {refresh.isPending ? null : <RotateCw className="size-4" aria-hidden />}
              Refresh assets
            </Button>
          ) : null}
          <BookmarkButton story={row} appearance="label" />
          {menu}
        </div>
      </div>
      <DeleteStoryDialog open={confirmingDelete} onOpenChange={setConfirmingDelete} stories={[{ uid, title: row.title, status: row.status }]} onDeleted={afterDelete} />

      {failed ? (
        <section className="card flex flex-col gap-4 p-6" role="alert" data-testid="story-failed">
          <div><TopicBadge story={row} /></div>
          <h1 className="text-2xl font-semibold text-ink">That one didn’t work.</h1>
          <p className="text-ink-2">Something went wrong while the story was being made. Trying again usually works.</p>
          <div className="flex flex-wrap gap-3">
            <Button variant="primary" size="lg" onClick={() => retry.mutate(row)} loading={retry.isPending}>
              {retry.isPending ? null : <RotateCcw aria-hidden />}
              Try again
            </Button>
            <Button asChild variant="secondary" size="lg">
              <Link to="/stories/new" search={wizardSearchFor(row, { idea: true })}>
                <Pencil aria-hidden />
                Change something
              </Link>
            </Button>
          </div>
          {retry.error ? <p className="text-sm text-danger">{retry.error instanceof Error ? retry.error.message : 'It could not be started again.'}</p> : null}
          <details className="text-sm text-ink-3">
            <summary className="cursor-pointer">What went wrong?</summary>
            <p className="mt-2 whitespace-pre-wrap break-words">{progress.jobFailed ?? storyError(row) ?? 'No details were recorded.'}</p>
          </details>
        </section>
      ) : !ready || (search.step === 'generate' && !progress.playable) ? (
        <section className="card flex flex-col gap-5 p-6" data-testid="story-making">
          <div>
            <TopicBadge story={row} />
            <h1 className="text-2xl font-semibold text-ink">{progress.paused ? 'Waiting for AI' : ready ? 'Almost ready…' : 'Making your story…'}</h1>
            <p className="mt-1 text-ink-2">“{row.prompt}”</p>
          </div>
          <StoryPhases step={progress.step} msg={progress.msg} paused={progress.paused} />
          {!ready && progress.playable ? (
            <div className="flex flex-wrap gap-3">
              <Button asChild variant="primary" size="lg">
                <Link to="/stories/$uid" params={{ uid }} search={{ step: 'play' }}>
                  <Play aria-hidden />
                  Preview ready scenes
                </Link>
              </Button>
            </div>
          ) : null}
          <p className="text-sm text-ink-3" data-testid="story-making-note">
            {progress.paused
              ? 'Your story is saved and queued. Writing resumes when AI is available. You can leave this page and come back.'
              : makingNote(row, { watchable: !ready && progress.playable, autoStart: search.step === 'generate' })}
          </p>
        </section>
      ) : (
        <ReadyStory row={row} onRefresh={() => refresh.mutate()} refreshing={refresh.isPending} />
      )}
      <StoryMetrics story={row} />
    </div>
  )
}

/**
 * How soon a longer story can be previewed: its first part is available after the plan and
 * one part are written (Long ≈ 45 s, Medium ≈ 35 s — docs/STORY-STREAMING.md), not after all of
 * them. Null for a one-part story, which is ready as a whole.
 */
function firstPartIn(row: Pick<StoryRow, 'length' | 'parts'>): string | null {
  if (row.length === 'long') return 'about a minute'
  if (row.length === 'medium' || (row.parts?.length ?? 0) > 1) return 'under a minute'
  return null
}

/** The small print under the steps: when it starts, and that the page can be left. */
export function makingNote(row: Pick<StoryRow, 'status' | 'length' | 'parts' | 'stream'>, { watchable, autoStart }: { watchable: boolean; autoStart: boolean }): string {
  const leave = 'You can leave this page and come back — it keeps going.'
  const auto = autoStart ? 'Playback starts when every scene is ready, without generation pauses. ' : ''
  if (watchable) return row.stream?.state === 'paused' ? `${auto}${leave}` : `${auto}Optional previews may pause while the remaining scenes are made. ${leave}`
  const soon = row.status === 'generating' ? firstPartIn(row) : null
  return soon ? `The first scenes can be previewed in ${soon}. ${auto}${leave}` : `${auto}${leave}`
}

function ReadyStory({ row, onRefresh, refreshing }: { row: StoryRow; onRefresh: () => void; refreshing: boolean }) {
  const feed = useQuery(puppetsQuery({}))
  const names = (row.cast ?? []).map((c) => c.name || feed.data?.items.find((p) => p.uid === c.uid)?.name || prettyUid(c.uid)).filter(Boolean)
  const length = storyLengthLine(row)
  // "Play" is this page's one big action: have the bundle, its first scene and the player's code
  // on their way before it is pressed. A story that streamed may still have a copy prefetched
  // while it was being made (the making screen, the Stories list's "Watch now"): that one is
  // dropped, not reused.
  const streamed = row.stream != null
  useEffect(() => prefetchStory(row.uid, streamed ? { fresh: true } : {}), [row.uid, streamed])
  return (
    <section className="card flex flex-col gap-5 overflow-hidden p-6" data-testid="story-ready">
      {/* The picture plays it too; the button below is the keyboard's way in. */}
      <Link to="/stories/$uid" params={{ uid: row.uid }} search={{ step: 'play' }} tabIndex={-1} aria-hidden className="group -mx-6 -mt-6 block">
        <StoryThumb story={row} playable size="hero" />
      </Link>
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-ink">{row.title}</h1>
        <GenerationModeBadge mode={row.generation_mode} />
        <TopicBadge story={row} />
        {length ? (
          <p className="mt-1 text-sm text-ink-3" data-testid="story-length">
            {length}
          </p>
        ) : null}
        <p className="mt-2 text-ink-2">{row.prompt}</p>
        {names.length ? <p className="mt-2 text-sm text-ink-3">With {names.join(', ')}</p> : null}
      </div>
      {row.learning_objective && <section aria-label="Saved learning objective" className="rounded-xl border border-line p-4">
        <LessonDetails card={row.learning_objective} ageBand={row.age_band} />
      </section>}
      <div className="flex flex-wrap gap-3">
        <Button asChild variant="primary" size="xl">
          <Link to="/stories/$uid" params={{ uid: row.uid }} search={{ step: 'play' }}>
            <Play aria-hidden />
            Play the story
          </Link>
        </Button>
        <VideoExport uid={row.uid} />
        <Button asChild variant="secondary" size="xl">
          <Link to="/stories/new" search={wizardSearchFor(row)}>
            <Sparkles aria-hidden />
            Another with the same friends
          </Link>
        </Button>
        <Button
          variant="outline"
          size="xl"
          onClick={onRefresh}
          loading={refreshing}
          disabled={refreshing}
          title="Refresh puppet animations, textures, and stages from the studio"
        >
          {refreshing ? null : <RotateCw aria-hidden />}
          Refresh assets
        </Button>
      </div>
    </section>
  )
}

/** `char:animals/forest/red_fox` → "Red fox"; `skel:…` → "A puppet". */
function prettyUid(uid: string): string {
  if (uid.startsWith('skel:')) return 'a puppet'
  const tail = uid.split('/').pop() ?? uid
  const t = tail.replace(/[_-]+/g, ' ').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : uid
}
